"use client";

import React, { useState } from "react";
import * as Sentry from "@sentry/nextjs";
import { OctagonAlertIcon, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { track } from "@/lib/analytics/track";

export function ReportIssueFloatingCard() {
  const [isOpen, setIsOpen] = useState(true);
  const [email, setEmail] = useState("");
  const [description, setDescription] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      Sentry.captureMessage(
        `User reported an issue from floating card. Email: ${email}, Description: ${description}`,
        "info"
      );
      track("report_issue_submitted");
      setSubmitted(true);
    } catch (err: any) {
      setFormError("Failed to send feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <div
        className={
          "transition-all duration-300" +
          (isOpen ? " pointer-events-auto opacity-100" : " pointer-events-none scale-95 opacity-0")
        }
      >
        <Card
          className={cn(
            "w-full max-w-md overflow-hidden rounded-lg border p-0 shadow transition-all duration-300",
            isOpen ? "max-h-[600px] scale-100" : "h-0 max-h-0 scale-95 p-0"
          )}
        >
          <CardHeader className="flex flex-row items-start justify-between px-6 pb-2 pt-4">
            <div>
              <CardTitle className="text-base">Report an issue</CardTitle>
              <CardDescription className="mb-2 mt-1 text-sm text-muted-foreground">
                We are sorry for the inconvenience. Our team has been notified of this issue. Please
                try again, or contact support if the problem persists.
              </CardDescription>
            </div>
            <button
              aria-label="Close"
              className="ml-2 mt-1 text-muted-foreground transition hover:text-foreground"
              onClick={() => setIsOpen(false)}
              tabIndex={0}
              type="button"
            >
              <X size={18} />
            </button>
          </CardHeader>
          <CardContent className="px-6 pb-6">
            {submitted ? (
              <div className="text-green-600">Thank you for your feedback!</div>
            ) : (
              <form className="space-y-4" onSubmit={handleSubmit}>
                <Input
                  type="email"
                  placeholder="Your email (optional)"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                />
                <Textarea
                  placeholder="Describe what you were doing or what happened..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  minLength={10}
                  disabled={submitting}
                />
                {formError && <div className="text-sm text-orange-600">{formError}</div>}
                <Button
                  type="submit"
                  variant="default"
                  className="w-full"
                  disabled={submitting || !description.trim()}
                >
                  {submitting ? "Submitting..." : "Submit"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
      {!isOpen && (
        <Button
          variant="destructive"
          size="icon"
          className="fixed bottom-6 right-6 z-50 size-12 rounded-full border-2 border-orange-400 shadow-lg"
          aria-label="Open feedback"
          onClick={() => setIsOpen(true)}
        >
          <OctagonAlertIcon className="size-6" />
        </Button>
      )}
    </div>
  );
}
